/*
 * Which workbench mode a picture belongs to (docs/ANALYSIS_WORKBENCH.md
 * section 4). Pure, but for sniffFile, which reads a file's first bytes.
 *
 * A "source" is what was opened:
 *   {kind: 'file', name, type, size, head}   a file picked or dropped
 *                                            (sniffFile: head = its first
 *                                            128 KB, never decoded as a
 *                                            picture, so a radiometric file
 *                                            still reaches the thermal upload
 *                                            byte for byte)
 *   {kind: 'photo', photo_type, plate_id}    a bank photograph
 *   {kind: 'thermalCapture'}                 a thermal capture
 *   {kind: 'micrograph'}                     a micrograph
 *
 * Each mode scores a source 0..1 (modes.js `detect`); detectMode takes the
 * best at or above SWITCH_AT, else nobody: the caller stays where it is.
 */
export const SNIFF_BYTES = 128 * 1024;
export const SWITCH_AT = 0.5;

const FLIR_TAG = [0x46, 0x4c, 0x49, 0x52, 0x00];   // "FLIR\0"
const FFF_MAGICS = ['FFF\0', 'AFF\0'];
const RAW_DATA = 0x0001;

/** The first `bytes` of a file or blob as a Uint8Array; null when it cannot be read. */
export async function readHead(file, bytes = SNIFF_BYTES) {
  try {
    const part = typeof file?.slice === 'function' ? file.slice(0, bytes) : file;
    if (part && typeof part.arrayBuffer === 'function') return new Uint8Array(await part.arrayBuffer());
    if (part && typeof FileReader !== 'undefined') {
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(new Uint8Array(reader.result));
        reader.onerror = () => resolve(null);
        reader.readAsArrayBuffer(part);
      });
    }
  } catch {
    /* unreadable: detected by its name and type alone */
  }
  return null;
}

/** A file as detection sees it. */
export async function sniffFile(file) {
  return {
    kind: 'file',
    name: String(file?.name || ''),
    type: String(file?.type || '').split(';')[0].trim().toLowerCase(),
    size: Number(file?.size) || 0,
    head: await readHead(file),
  };
}

export const isJpeg = head => !!head && head.length > 3 && head[0] === 0xff && head[1] === 0xd8;
export const isTiff = head => !!head && head.length > 3
  && ((head[0] === 0x49 && head[1] === 0x49 && head[2] === 0x2a && head[3] === 0x00)
      || (head[0] === 0x4d && head[1] === 0x4d && head[2] === 0x00 && head[3] === 0x2a));

/** A JPEG's marker segments up to the scan: [{marker, start, end}] (start: after the length word). */
export function jpegSegments(head) {
  const out = [];
  if (!isJpeg(head)) return out;
  let at = 2;
  while (at + 4 <= head.length) {
    if (head[at] !== 0xff) break;
    const marker = head[at + 1];
    if (marker === 0xff) { at += 1; continue; }
    if (marker === 0xd9 || marker === 0xda) break;
    if ((marker >= 0xd0 && marker <= 0xd7) || marker === 0x01) { at += 2; continue; }
    const length = (head[at + 2] << 8) | head[at + 3];
    if (length < 2) break;
    out.push({ marker, start: at + 4, end: Math.min(head.length, at + 2 + length) });
    at += 2 + length;
  }
  return out;
}

const startsWith = (head, at, bytes) => bytes.every((b, i) => head[at + i] === b);
/** `n` bytes from `at` as text, one character per byte (in pieces: a spread of 128 K arguments overflows). */
function ascii(head, at, n) {
  let out = '';
  const end = Math.min(head.length, at + n);
  for (let i = at; i < end; i += 8192) out += String.fromCharCode(...head.subarray(i, Math.min(end, i + 8192)));
  return out;
}
const u32 = (head, at, little) => (little
  ? (head[at] | (head[at + 1] << 8) | (head[at + 2] << 16) | (head[at + 3] << 24)) >>> 0
  : ((head[at] << 24) | (head[at + 1] << 16) | (head[at + 2] << 8) | head[at + 3]) >>> 0);
const u16 = (head, at, little) => (little ? head[at] | (head[at + 1] << 8) : (head[at] << 8) | head[at + 1]);

/**
 * What a JPEG's FLIR APP1 segments say: 'none' (no FLIR segment), 'radiometric'
 * (its FFF directory has a RawData record, read as the backend's thermal/flir.py
 * fff_records does) or 'flir' (FLIR segments whose directory is not in the
 * bytes read, or holds no RawData: a FLIR camera's visual photograph).
 */
export function flirKind(head) {
  const segments = jpegSegments(head).filter(s => s.marker === 0xe1 && s.end - s.start >= 8
    && startsWith(head, s.start, FLIR_TAG));
  if (!segments.length) return 'none';
  const first = segments.find(s => head[s.start + 6] === 0) || segments[0];
  const fff = first.start + 8;
  const chunkEnd = first.end;
  if (fff + 0x40 > chunkEnd || !FFF_MAGICS.includes(ascii(head, fff, 4))) return 'flir';
  const version = u32(head, fff + 0x14, false);
  const little = !(version >= 100 && version < 200);
  const offset = u32(head, fff + 0x18, little);
  const count = u32(head, fff + 0x1c, little);
  if (count > 1024) return 'flir';
  for (let k = 0; k < count; k += 1) {
    const at = fff + offset + 32 * k;
    if (at + 32 > chunkEnd) return 'flir';
    const type = u16(head, at, little);
    const length = u32(head, at + 16, little);
    if (type === RAW_DATA && length > 0) return 'radiometric';
  }
  return 'flir';
}

/** Whether the first bytes name a microscope's own software (cellSens, Olympus, Evident). */
export function microscopeMarks(head) {
  if (!head || !head.length) return false;
  const text = ascii(head, 0, Math.min(head.length, SNIFF_BYTES)).toLowerCase();
  return ['cellsens', 'olympus', 'evident scientific'].some(word => text.includes(word));
}

const extensionOf = name => (String(name).includes('.') ? String(name).split('.').pop().toLowerCase() : '');
const isPicture = source => /^image\//.test(source.type || '')
  || ['jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'heic', 'bmp'].includes(extensionOf(source.name));

// ── each mode's score ─────────────────────────────────────────────────────────

export function thermalScore(source) {
  if (!source) return 0;
  if (source.kind === 'thermalCapture') return 1;
  if (source.kind !== 'file') return 0;
  const kind = flirKind(source.head);
  if (kind === 'radiometric') return 1;
  if (kind === 'flir') return 0.4;
  return 0;
}

export function microscopyScore(source) {
  if (!source) return 0;
  if (source.kind === 'micrograph') return 1;
  if (source.kind === 'photo') return source.photo_type === 'microscopy' ? 1 : 0;
  if (source.kind !== 'file') return 0;
  if (extensionOf(source.name) === 'vsi') return 0.9;
  if ((isTiff(source.head) || isJpeg(source.head)) && microscopeMarks(source.head)) return 0.9;
  return 0;
}

export function cultureScore(source) {
  if (!source) return 0;
  if (source.kind === 'photo') {
    if (source.photo_type === 'microscopy') return 0;
    return source.photo_type === 'plate_photo' || source.plate_id ? 1 : 0;
  }
  if (source.kind !== 'file') return 0;
  return isPicture(source) ? 0.3 : 0;
}

/**
 * The key of the mode `source` belongs to, among `modes` ([{key, detect}]),
 * or null when none scores SWITCH_AT or more. A tie goes to the first listed.
 */
export function detectMode(source, modes) {
  let best = null;
  let bestScore = 0;
  for (const mode of modes || []) {
    const score = typeof mode.detect === 'function' ? Number(mode.detect(source)) || 0 : 0;
    if (score > bestScore) {
      best = mode.key;
      bestScore = score;
    }
  }
  return bestScore >= SWITCH_AT ? best : null;
}
